'use client'

import { useEffect, useState, useTransition } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Text, InputGroup, InputLeftElement, Icon, InputGroupProps } from '@/components/Chakra'
import { HiSearch } from 'react-icons/hi'
import Autosuggest, { SuggestionsFetchRequestedParams } from 'react-autosuggest'
import { fetchFeedSuggestionsAction, fetchFeedSearchHistoriesAction } from '../actions'
import { useServerActionHandler } from '@/hooks/useServerActionHandler'

type KeywordBoxProps = InputGroupProps

type Section = {
  title: string
  items: string[]
}

const MAX_SUGGESTIONS_CHAR_LENGTH = 30

export const KeywordBox = ({ ...inputGroupProps }: KeywordBoxProps) => {
  const serverActionHandler = useServerActionHandler()
  const [isPending, startTrasition] = useTransition()
  const [suggestions, setSuggestions] = useState<Section>()
  const [histories, setHistories] = useState<Section>()

  const [keyword, setKeyword] = useState('')
  const searchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    fetchFeedSearchHistoriesAction()
      .then(serverActionHandler.responseHandler)
      .then((res) => {
        setHistories({
          title: '履歴',
          items: res.payload,
        })
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const query = searchParams.get('q')
    const hashtag = searchParams.get('hashtag')
    if (query) {
      setKeyword(hashtag ? `#${query}` : query)
    }
  }, [searchParams])

  const getSections: () => Section[] = () => {
    const sections = [suggestions, histories].filter((section) => section !== undefined) as Section[]
    return sections
      .filter((section) => section.items.length > 0)
      .filter((section) => keyword === '' || section.title === '候補')
  }

  const updateKeyword = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString())
    if (value != '') {
      newSearchParams.set('q', value.replace(/#/g, ''))
      if (value[0] === '#') {
        newSearchParams.set('hashtag', '1')
      } else {
        newSearchParams.delete('hashtag')
      }
    } else {
      newSearchParams.delete('hashtag')
      newSearchParams.delete('q')
    }

    newSearchParams.delete('page')
    router.push(`/feeds?${newSearchParams}`)
  }

  const getSuggestionValue = (suggestion: string) => suggestion

  const onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
    if (isPending) return
    startTrasition(() => {
      if (keyword === '') {
        setSuggestions({
          title: '候補',
          items: [],
        })
        return
      }
      fetchFeedSuggestionsAction({ keyword: params.value })
        .then(serverActionHandler.responseHandler)
        .then((res) => {
          setSuggestions({
            title: '候補',
            items: res.payload,
          })
        })
        .catch(() => {})
    })
  }

  return (
    <InputGroup {...inputGroupProps}>
      <InputLeftElement pointerEvents="none">
        <Icon as={HiSearch} color="gray.400" />
      </InputLeftElement>
      <Autosuggest
        suggestions={getSections()}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={() => {}}
        onSuggestionSelected={(_e, params) => {
          updateKeyword(params.suggestionValue)
        }}
        multiSection={true}
        renderSectionTitle={(section) => <Text>{section.title}</Text>}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={(suggestion: string) => <Text>{suggestion.substr(0, MAX_SUGGESTIONS_CHAR_LENGTH)}</Text>}
        containerProps={{
          style: {
            width: '100%',
          },
        }}
        shouldRenderSuggestions={(value) => value.length >= 0}
        getSectionSuggestions={(section) => section.items}
        inputProps={{
          placeholder: 'キーワードで検索',
          value: keyword,
          onChange: (_e, params) => {
            setKeyword(params.newValue)
          },
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.nativeEvent.isComposing || e.key !== 'Enter') return
            updateKeyword(keyword)
          },
          style: {
            paddingLeft: '2.5rem',
          },
        }}
      />
    </InputGroup>
  )
}
